import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { scrollTo } from '@ucheba/utils/helpers/components'
import { feedbacksThunks } from '@ucheba/store/feedbacks'
import { questionsThunks } from '@ucheba/store/questions'
import { IInstitutionQuestion } from '@ucheba/store/institutions/questions/types'

import { removeQueryParameters, stringifyUrl } from '@ucheba/utils/helpers/router'
import { authSelectors } from '@ucheba/store/auth'
import { useAuthHref } from '@ucheba/utils/hooks/useAuthHref'
import { getParsedCookies } from '@ucheba/utils/helpers/core'
import { authCookieToken } from '@ucheba/utils/constants/core'
import {
  TReducedDataList,
  TListItem,
  IUseCommunicationForm,
  TUseResponsesList,
  EFormType,
  IUsersCommunicationProps,
} from './types'
import { questionHighlighting, questionHighlightingParams } from './constants'

const getResponses = (items, parentId): TListItem[] => {
  /*
    Получаем список ответов от рутового (responses)
    Он может быть глубогим (иерархически вложенным) и генерим "удобные" ответы
    Если хоть у одного есть ответы (responses), то рекурсимся,
    Причем, рекурсимся, не забываея текущий (см. if (responses?.length > 0))
    На выходе может получится вложенная структура,
    поэтому она сплющиваем flat(Infinity)
  */
  return items?.map((item) => {
    const { responses, ...other } = item

    if (responses?.length > 0) {
      return [{ ...other, parentId }, getResponses(responses, item.owner.id)]
    }

    return { ...other, parentId }
  })
}

/*
  Что здесь происходит:
    reduceDataList принимает список вопросов,
    у каждого из которых может быть связанная с ним иерархия ответов (ответ на ответ и т.д.)
    reduceDataList возвращает тоже список, но каждый объект которго приведен к удобному виду
    с плоской структурой ответов:
    [
      {
        root: {
          это верхнеуровневый вопрос (без свойства responses)
        },
        responses: [
          { ответ (та же структура что и root (без свойства responses) },
          { ответ (та же структура что и root (без свойства responses) },
          { ответ (та же структура что и root (без свойства responses) },
          ...
        ]
      },
      ...
    ]
*/
export const reduceDataList = (questions: IInstitutionQuestion[]): TReducedDataList => {
  if (!questions) return []

  return questions?.map((question) => {
    const {
      id,
      text,
      dateCreated,
      rate,
      owner,
      faculty,
      program,
      voteStats,
      responseFor,
    } = question

    const rootResponses = question.responses

    return {
      root: {
        id,
        text,
        dateCreated,
        rate,
        owner,
        faculty,
        program,
        voteStats,
        responseFor,
        parentId: null,
      },
      responses: getResponses(rootResponses, owner.id)?.flat(Infinity),
    }
  })
}

export const useCommunicationForm: IUseCommunicationForm = (
  type,
  list,
  reqType,
  callback,
  limit,
  offset,
  onAfterSubmit
) => {
  const [error, setError] = useState<string | null>(null)
  const dispatch = useDispatch()

  const isFeedbackType = type === 'responsesFeedbacks' || type === 'feedbacks'

  const onSubmit = useCallback(
    async (
      text: string,
      isPublished: boolean,
      parentId: number | null,
      rate: number | undefined,
      id: number
    ) => {
      const thunk = isFeedbackType ? feedbacksThunks : questionsThunks

      const result = await dispatch(
        thunk.send({
          data: {
            type: reqType,
            text,
            isPublished,
            parentId,
            id,
            rate,
          },
        })
      )

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (result?.payload?.error) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setError(result.payload.error)
      } else {
        const data = { type: reqType, id } as Record<string, any>

        if (typeof limit === 'number') {
          data.limit = limit
        }

        if (typeof offset === 'number') {
          data.offset = offset
        }
        if (onAfterSubmit && !parentId) {
          onAfterSubmit()
        } else {
          await dispatch(
            thunk.fetch({
              data,
            })
          )
        }
        if (callback) {
          callback()
        }

        setError(null)
      }
    },
    [dispatch, list?.length, callback, isFeedbackType, reqType, limit, offset]
  )

  return {
    setError,
    onSubmit,
    error,
  }
}

export const useResponsesList: TUseResponsesList = (responses, parentId) => {
  const [showAllAnswers, setShowAllAnswers] = useState(false)

  const responsesList = useMemo(
    () => (!showAllAnswers && responses?.length > 1 ? [responses[0]] : responses),
    [responses, showAllAnswers]
  )

  const { query } = useRouter()

  const queryParentId = query.parentId as string
  const queryQuestionId = query.questionId as string

  useEffect(() => {
    if (queryParentId === parentId) setShowAllAnswers(true)
  }, [])

  useEffect(() => {
    if (queryQuestionId || (queryQuestionId && showAllAnswers))
      setTimeout(() => {
        const item = document.getElementById(`${queryQuestionId}`)
        item?.animate(questionHighlighting, questionHighlightingParams)

        scrollTo(queryQuestionId as string, 'center')
      }, 1000)
  }, [queryQuestionId, showAllAnswers])

  return {
    responsesList,
    showAllAnswers,
    setShowAllAnswers,
  }
}

interface IUseUsersCommunication {
  (props: IUsersCommunicationProps): {
    isDialogOpen: boolean
    parentId: null | number
    formType: EFormType
    isQuestions: boolean
    onOpenCommunicationForm: () => void
    onAnswerClick: (id: number) => void
    onDialogClose: () => void
    showBanner: (index: number) => boolean
  }
}

export const useUsersCommunication: IUseUsersCommunication = (props) => {
  const { type, items, isBanner = false } = props

  const isAuth = useSelector(authSelectors.isValidToken)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [parentId, setParentId] = useState<null | number>(null)
  const [formType, setFormType] = useState(EFormType[type])
  const { push, query, asPath, replace } = useRouter()
  const isQuestions = type === EFormType.questions

  const onOpenCommunicationForm = useCallback((): void => {
    const parsedCookie = getParsedCookies()
    const tokenString = parsedCookie[authCookieToken]

    if (!isAuth || !tokenString) {
      const queryParams: any = { ...query }

      if (type === EFormType.responsesQuestions) {
        queryParams.questionsBlock = 1
      }

      if (type === EFormType.responsesFeedbacks) {
        queryParams.feedbacksBlock = 1
      }

      if (type === EFormType.questions) {
        queryParams.questions = 1
      }

      if (type === EFormType.feedbacks) {
        queryParams.feedbacks = 1
      }

      push(
        stringifyUrl(`/auth`, {
          update: {
            redirect: stringifyUrl(asPath, {
              update: {
                ...queryParams,
              },
            }),
          },
        }),
        undefined,
        {}
      )

      return
    }

    setFormType(EFormType[type])
    setParentId(null)
    setIsDialogOpen(true)
  }, [type, isAuth, query, asPath, push])

  useEffect(() => {
    if (isAuth) {
      if (query.questionsBlock && type === EFormType.responsesQuestions) {
        onOpenCommunicationForm()

        scrollTo('questions')
      }

      if (query.questions && type === EFormType.questions) {
        onOpenCommunicationForm()

        scrollTo('questions')
      }

      if (query.feedbacksBlock && type === EFormType.responsesFeedbacks) {
        onOpenCommunicationForm()

        scrollTo('feedbacks')
      }

      if (query.feedbacks && type === EFormType.feedbacks) {
        onOpenCommunicationForm()

        scrollTo('feedbacks')
      }
    }
  }, [])

  const onAnswerClick = useCallback(
    (id): void => {
      const parsedCookie = getParsedCookies()
      const tokenString = parsedCookie[authCookieToken]

      if (!isAuth || !tokenString) {
        push(
          stringifyUrl('/auth', {
            set: {
              redirect: stringifyUrl(removeQueryParameters(asPath), {
                ...query,
              }),
            },
          })
        )

        return
      }

      setFormType(
        isQuestions ? EFormType.responsesQuestions : EFormType.responsesFeedbacks
      )
      setParentId(id)
      setIsDialogOpen(true)
    },
    [asPath, isAuth, isQuestions, push, query]
  )

  const onDialogClose = useCallback((): void => {
    setIsDialogOpen(false)

    if (query.feedbacksBlock && type === EFormType.responsesFeedbacks) {
      replace(
        stringifyUrl(removeQueryParameters(asPath), {
          update: {
            ...query,
          },
          exclude: ['feedbacksBlock'],
        }),
        undefined,
        { shallow: true, scroll: false }
      )
    }

    if (query.feedbacks && type === EFormType.feedbacks) {
      replace(
        stringifyUrl(removeQueryParameters(asPath), {
          update: {
            ...query,
          },
          exclude: ['feedbacks'],
        }),
        undefined,
        { shallow: true, scroll: false }
      )
    }

    if (query.questionsBlock && type === EFormType.responsesQuestions) {
      replace(
        stringifyUrl(removeQueryParameters(asPath), {
          update: {
            ...query,
          },
          exclude: ['questionsBlock'],
        }),
        undefined,
        { shallow: true, scroll: false }
      )
    }

    if (query.questions && type === EFormType.questions) {
      replace(
        stringifyUrl(removeQueryParameters(asPath), {
          update: {
            ...query,
          },
          exclude: ['questions'],
        }),
        undefined,
        { shallow: true, scroll: false }
      )
    }
  }, [asPath, query, replace, type])

  const showBanner = useCallback(
    (index: number) => {
      if (isBanner && items && items?.length < 3) {
        return items.length - 1 === index
      }
      if (isBanner && items) {
        return index === 2
      }
      return false
    },
    [items, isBanner]
  )

  return {
    onOpenCommunicationForm,
    onDialogClose,
    onAnswerClick,
    isDialogOpen,
    isQuestions,
    parentId,
    formType,
    showBanner,
  }
}
